import React, { useState, useEffect, useCallback, useRef } from 'react';
import { folderApi, cloudProjectApi } from '../../services/cloudApi';
import { PublishModal } from '../PublishModal';
import { overlayDismissProps } from '../../utils/overlayDismiss';

/**
 * BuilderProjectsGrid - Shows project cards with folder organization
 * Uses server-side search and pagination for performance
 */
function BuilderProjectsGrid({ onSelectProject, onCreateNew }) {
  // Search state - debounced server-side search
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef(null);
  
  // Projects state - loaded from server
  const [projects, setProjects] = useState([]);
  const [projectsTotal, setProjectsTotal] = useState(0);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  
  const [loadingProjectId, setLoadingProjectId] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [isLoadingFolders, setIsLoadingFolders] = useState(true);
  
  // Modal states
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [folderColor, setFolderColor] = useState('#60D2CC');
  const [isSavingFolder, setIsSavingFolder] = useState(false);
  
  // Drag and drop
  const [draggedProject, setDraggedProject] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  
  // Multi-select state
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState(null); // { x, y, project }
  
  // Rename modal state
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renamingProject, setRenamingProject] = useState(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  
  // Move to folder modal
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [movingProjects, setMovingProjects] = useState([]);
  const [isMoving, setIsMoving] = useState(false);
  
  // Bulk delete
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Confirmation modal
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, onConfirm, danger }
  
  // Publish modal
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishingProject, setPublishingProject] = useState(null);

  // Debounce search input (300ms delay)
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1); // Reset to first page on new search
    }, 300);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Load projects from server (with search, folder filter, pagination)
  const loadProjects = useCallback(async () => {
    setIsLoadingProjects(true);
    try {
      const folderId = selectedFolderId === 'uncategorized' ? null : selectedFolderId;
      const res = await cloudProjectApi.list({
        folderId: folderId === null ? undefined : folderId,
        search: debouncedSearch || undefined,
        page: currentPage,
        pageSize,
      });
      
      // For "uncategorized", filter client-side (backend doesn't support null folder filter)
      let projectList = res.projects || [];
      if (selectedFolderId === 'uncategorized') {
        projectList = projectList.filter(p => !p.folder_id);
      }
      
      setProjects(projectList);
      setProjectsTotal(res.total || 0);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setProjects([]);
    } finally {
      setIsLoadingProjects(false);
    }
  }, [debouncedSearch, selectedFolderId, currentPage, pageSize]);

  // Reload projects when search, folder, or page changes
  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Load folders
  const loadFolders = useCallback(async () => {
    setIsLoadingFolders(true);
    try {
      const res = await folderApi.list();
      setFolders(res.folders || []);
    } catch (err) {
      console.error('Failed to load folders:', err);
    } finally {
      setIsLoadingFolders(false);
    }
  }, []);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    const anyOpen = showFolderModal || showRenameModal || showMoveModal || !!confirmModal;
    if (!anyOpen) return;

    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (showFolderModal) setShowFolderModal(false);
      if (showRenameModal) setShowRenameModal(false);
      if (showMoveModal) setShowMoveModal(false);
      if (confirmModal) setConfirmModal(null);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showFolderModal, showRenameModal, showMoveModal, confirmModal]);

  const handleProjectClick = async (project) => {
    setLoadingProjectId(project.id);
    try {
      await onSelectProject(project);
    } finally {
      setLoadingProjectId(null);
    }
  };

  // Projects are already filtered server-side
  const filteredProjects = projects;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Folder CRUD
  const handleCreateFolder = () => {
    setEditingFolder(null);
    setFolderName('');
    setFolderColor('#60D2CC');
    setShowFolderModal(true);
  };

  const handleEditFolder = (folder, e) => {
    e.stopPropagation();
    setEditingFolder(folder);
    setFolderName(folder.name);
    setFolderColor(folder.color || '#60D2CC');
    setShowFolderModal(true);
  };

  const handleDeleteFolder = async (folder, e) => {
    e.stopPropagation();
    setConfirmModal({
      title: 'Delete Folder',
      message: `Delete folder "${folder.name}"? Projects will be moved to root.`,
      danger: true,
      onConfirm: async () => {
        try {
          await folderApi.delete(folder.id);
          await loadFolders();
          if (selectedFolderId === folder.id) {
            setSelectedFolderId(null);
          }
          loadProjects();
        } catch (err) {
          console.error('Failed to delete folder:', err);
        }
        setConfirmModal(null);
      }
    });
  };

  const handleSaveFolder = async () => {
    if (!folderName.trim()) return;
    
    setIsSavingFolder(true);
    try {
      if (editingFolder) {
        await folderApi.update(editingFolder.id, {
          name: folderName.trim(),
          color: folderColor,
        });
      } else {
        await folderApi.create(folderName.trim(), null, { color: folderColor });
      }
      await loadFolders();
      setShowFolderModal(false);
    } catch (err) {
      console.warn('Failed to save folder:', err.message);
    } finally {
      setIsSavingFolder(false);
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e, project) => {
    setDraggedProject(project);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', project.id);
  };

  const handleDragEnd = () => {
    setDraggedProject(null);
    setDragOverFolder(null);
  };

  const handleDragOver = (e, folderId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(folderId);
  };

  const handleDragLeave = () => {
    setDragOverFolder(null);
  };

  const handleDrop = async (e, folderId) => {
    e.preventDefault();
    setDragOverFolder(null);
    
    if (!draggedProject) return;
    
    // Don't move if already in this folder
    const targetFolderId = folderId === 'uncategorized' ? null : folderId;
    if (draggedProject.folder_id === targetFolderId) return;
    
    try {
      await cloudProjectApi.update(draggedProject.id, {
        folder_id: targetFolderId
      });
      // Reload projects after move
      loadProjects();
    } catch (err) {
      console.warn('Failed to move project:', err.message);
    }
    
    setDraggedProject(null);
  };

  // Close context menu on click outside
  useEffect(() => {
    const handleClickOutside = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  // Context menu handler
  const handleContextMenu = (e, project) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, project });
  };

  // Selection handlers
  const toggleProjectSelection = (projectId, e) => {
    e.stopPropagation();
    setSelectedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedProjects(new Set(filteredProjects.map(p => p.id)));
  };

  const selectNone = () => {
    setSelectedProjects(new Set());
  };

  const toggleSelectionMode = () => {
    if (isSelectionMode) {
      setSelectedProjects(new Set());
    }
    setIsSelectionMode(!isSelectionMode);
  };

  // Rename handler
  const handleRenameClick = (project) => {
    setRenamingProject(project);
    setNewProjectName(project.display_name || project.name);
    setShowRenameModal(true);
    setContextMenu(null);
  };

  const handleRenameSubmit = async () => {
    if (!newProjectName.trim() || !renamingProject) return;
    
    setIsRenaming(true);
    try {
      await cloudProjectApi.update(renamingProject.id, {
        display_name: newProjectName.trim()
      });
      await loadProjects();
      setShowRenameModal(false);
      setRenamingProject(null);
    } catch (err) {
      console.warn('Failed to rename project:', err.message);
    } finally {
      setIsRenaming(false);
    }
  };

  // Delete handler
  const handleDeleteClick = (project) => {
    setContextMenu(null);
    setConfirmModal({
      title: 'Delete Project',
      message: `Delete "${project.display_name || project.name}"? This action cannot be undone.`,
      danger: true,
      onConfirm: async () => {
        try {
          await cloudProjectApi.delete(project.id);
          await loadProjects();
        } catch (err) {
          console.error('Failed to delete project:', err);
        }
        setConfirmModal(null);
      }
    });
  };

  // Bulk delete
  const handleBulkDelete = () => {
    if (selectedProjects.size === 0) return;
    
    const count = selectedProjects.size;
    setConfirmModal({
      title: 'Delete Projects',
      message: `Delete ${count} project${count > 1 ? 's' : ''}? This action cannot be undone.`,
      danger: true,
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          await Promise.all([...selectedProjects].map(id => cloudProjectApi.delete(id)));
          setSelectedProjects(new Set());
          setIsSelectionMode(false);
          await loadProjects();
        } catch (err) {
          console.error('Failed to delete some projects:', err);
        } finally {
          setIsDeleting(false);
        }
        setConfirmModal(null);
      }
    });
  };

  // Move to folder handlers
  const handleMoveClick = (project) => {
    setMovingProjects([project]);
    setShowMoveModal(true);
    setContextMenu(null);
  };

  const handleBulkMove = () => {
    if (selectedProjects.size === 0) return;
    const projectsToMove = filteredProjects.filter(p => selectedProjects.has(p.id));
    setMovingProjects(projectsToMove);
    setShowMoveModal(true);
  };

  const handleMoveToFolder = async (folderId) => {
    if (movingProjects.length === 0) return;
    
    setIsMoving(true);
    try {
      const targetFolderId = folderId === 'uncategorized' ? null : folderId;
      await Promise.all(movingProjects.map(p => 
        cloudProjectApi.update(p.id, { folder_id: targetFolderId })
      ));
      setSelectedProjects(new Set());
      setIsSelectionMode(false);
      setShowMoveModal(false);
      setMovingProjects([]);
      await loadProjects();
    } catch (err) {
      console.warn('Failed to move projects:', err.message);
    } finally {
      setIsMoving(false);
    }
  };

  // Count projects per folder
  // Note: For accurate counts across folders, we'd need backend support
  // For now, show total for current view
  const getProjectCount = (folderId) => {
    if (folderId === selectedFolderId) {
      return projectsTotal;
    }
    // For non-selected folders, don't show count (would need separate API calls)
    return '';
  };
  
  // Calculate pagination
  const totalPages = Math.ceil(projectsTotal / pageSize);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  return (
    <div className="builder-projects-grid-screen">
      {/* Background Grid Effect */}
      <div className="builder-bg-grid" />

      <div className="builder-projects-with-sidebar">
        {/* Folder Sidebar */}
        <div className="builder-folders-sidebar">
          <div className="folders-sidebar-header">
            <h3>Folders</h3>
            <button 
              className="folder-add-btn" 
              onClick={handleCreateFolder}
              title="Create folder"
            >
              <PlusIcon />
            </button>
          </div>

          <div className="folders-list">
            {/* All Projects */}
            <div 
              className={`folder-item ${selectedFolderId === null ? 'active' : ''}`}
              onClick={() => setSelectedFolderId(null)}
              onDragOver={(e) => handleDragOver(e, null)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, null)}
            >
              <FolderIcon color="#60D2CC" />
              <span className="folder-name">All Projects</span>
              <span className="folder-count">{getProjectCount(null)}</span>
            </div>

            {/* Uncategorized */}
            <div 
              className={`folder-item ${selectedFolderId === 'uncategorized' ? 'active' : ''} ${dragOverFolder === 'uncategorized' ? 'drag-over' : ''}`}
              onClick={() => setSelectedFolderId('uncategorized')}
              onDragOver={(e) => handleDragOver(e, 'uncategorized')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'uncategorized')}
            >
              <FolderIcon color="#6b7280" />
              <span className="folder-name">Uncategorized</span>
              <span className="folder-count">{getProjectCount('uncategorized')}</span>
            </div>

            <div className="folders-divider" />

            {/* User Folders */}
            {isLoadingFolders ? (
              <div className="folders-loading">Loading...</div>
            ) : folders.length === 0 ? (
              <div className="folders-empty">
                <p>No folders yet</p>
                <button onClick={handleCreateFolder}>Create one</button>
              </div>
            ) : (
              folders.map(folder => (
                <div 
                  key={folder.id}
                  className={`folder-item ${selectedFolderId === folder.id ? 'active' : ''} ${dragOverFolder === folder.id ? 'drag-over' : ''}`}
                  onClick={() => setSelectedFolderId(folder.id)}
                  onDragOver={(e) => handleDragOver(e, folder.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, folder.id)}
                >
                  <FolderIcon color={folder.color || '#f59e0b'} />
                  <span className="folder-name">{folder.name}</span>
                  <span className="folder-count">{getProjectCount(folder.id)}</span>
                  <div className="folder-actions">
                    <button onClick={(e) => handleEditFolder(folder, e)} title="Edit">
                      <EditIcon />
                    </button>
                    <button onClick={(e) => handleDeleteFolder(folder, e)} title="Delete">
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="builder-projects-container">
          {/* Header */}
          <div className="builder-projects-header">
            <div className="builder-projects-title-section">
              <h1 className="builder-projects-title">
                {selectedFolderId === null ? 'All Projects' : 
                 selectedFolderId === 'uncategorized' ? 'Uncategorized' :
                 folders.find(f => f.id === selectedFolderId)?.name || 'Projects'}
              </h1>
              <p className="builder-projects-subtitle">
                {selectedFolderId === null 
                  ? 'All your projects in one place'
                  : selectedFolderId === 'uncategorized'
                  ? 'Projects not in any folder'
                  : 'Drag projects here to organize them'}
              </p>
            </div>
            
            <div className="builder-projects-actions">
              <div className="builder-projects-search">
                <SearchIcon />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <button 
                className={`builder-select-btn ${isSelectionMode ? 'active' : ''}`} 
                onClick={toggleSelectionMode}
                title={isSelectionMode ? 'Exit selection mode' : 'Select multiple'}
              >
                <CheckboxIcon checked={isSelectionMode} />
              </button>
              <button className="builder-create-btn" onClick={onCreateNew}>
                <PlusIcon /> Create New
              </button>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          {isSelectionMode && (
            <div className="builder-bulk-actions">
              <div className="bulk-select-controls">
                <button onClick={selectAll}>Select All</button>
                <button onClick={selectNone}>Select None</button>
                <span className="selection-count">
                  {selectedProjects.size} of {filteredProjects.length} selected
                </span>
              </div>
              {selectedProjects.size > 0 && (
                <div className="bulk-action-buttons">
                  <button onClick={handleBulkMove} disabled={isMoving}>
                    <FolderIcon color="currentColor" size={14} /> Move to Folder
                  </button>
                  <button onClick={handleBulkDelete} disabled={isDeleting} className="danger">
                    <TrashIcon /> Delete ({selectedProjects.size})
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Projects Grid */}
          <div className="builder-projects-cards">
            {isLoadingProjects ? (
              <div className="builder-projects-loading">
                <div className="builder-project-spinner" />
                <span>Loading projects...</span>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="builder-no-results">
                {searchQuery ? (
                  <p>No projects found matching "{searchQuery}"</p>
                ) : (
                  <p>No projects in this folder yet. Drag projects here or create a new one.</p>
                )}
              </div>
            ) : (
              filteredProjects.map(project => (
                <div
                  key={project.id}
                  className={`builder-project-card ${loadingProjectId === project.id ? 'loading' : ''} ${draggedProject?.id === project.id ? 'dragging' : ''} ${selectedProjects.has(project.id) ? 'selected' : ''}`}
                  onClick={(e) => {
                    if (isSelectionMode) {
                      toggleProjectSelection(project.id, e);
                    } else {
                      handleProjectClick(project);
                    }
                  }}
                  onContextMenu={(e) => handleContextMenu(e, project)}
                  draggable={!isSelectionMode}
                  onDragStart={(e) => handleDragStart(e, project)}
                  onDragEnd={handleDragEnd}
                >
                  {/* Loading overlay */}
                  {loadingProjectId === project.id && (
                    <div className="builder-project-loading">
                      <div className="builder-project-spinner" />
                      <span>Opening project...</span>
                    </div>
                  )}
                  
                  {/* Selection checkbox */}
                  {isSelectionMode && (
                    <div 
                      className="project-checkbox"
                      onClick={(e) => toggleProjectSelection(project.id, e)}
                    >
                      <CheckboxIcon checked={selectedProjects.has(project.id)} />
                    </div>
                  )}
                  
                  {/* Drag handle indicator */}
                  {!isSelectionMode && (
                    <div className="project-drag-handle">
                      <DragIcon />
                    </div>
                  )}
                  
                  {/* Thumbnail */}
                  <div className="builder-project-thumbnail">
                    {project.thumbnail ? (
                      <img 
                        src={project.thumbnail} 
                        alt={project.display_name || project.name}
                      />
                    ) : (
                      <div className="builder-thumbnail-placeholder">
                        <ProjectIcon />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="builder-project-info">
                    <div className="builder-project-header">
                      <h3 className="builder-project-name">
                        {project.display_name || project.name}
                      </h3>
                      {project.is_published && (
                        <span className="builder-project-live-badge" title={project.preview_url ? `Live at ${project.preview_url}` : 'Published'}>
                          <span className="live-dot"></span>
                          Live
                        </span>
                      )}
                    </div>
                    {project.display_name && project.display_name !== project.name && (
                      <span className="builder-project-tech-name">{project.name}</span>
                    )}
                    <div className="builder-project-meta">
                      <span className="builder-project-date">
                        <ClockIcon /> {formatDate(project.updated_at)}
                      </span>
                      {project.template && (
                        <span className="builder-project-template">
                          {project.template === 'unlimited3d'
                            ? 'Threedium'
                            : project.template === 'gamekit'
                              ? 'Game Studio'
                              : project.template === 'r3f'
                                ? 'R3F'
                                : project.template === 'threejs'
                                  ? 'Three.js'
                                  : 'React'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="builder-pagination">
              <button 
                className="pagination-btn"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={!hasPrevPage}
              >
                ← Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages} ({projectsTotal} projects)
              </span>
              <button 
                className="pagination-btn"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={!hasNextPage}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Folder Modal */}
      {showFolderModal && (
        <div className="folder-modal-overlay" {...overlayDismissProps(() => setShowFolderModal(false))}>
          <div className="folder-modal" onClick={e => e.stopPropagation()}>
            <h2>{editingFolder ? 'Edit Folder' : 'Create Folder'}</h2>
            
            <div className="folder-modal-form">
              <div className="form-group">
                <label>Folder Name</label>
                <input
                  type="text"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  placeholder="My Folder"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Color</label>
                <div className="color-picker">
                  {['#60D2CC', '#f59e0b', '#ef4444', '#8b5cf6', '#22c55e', '#3b82f6', '#ec4899'].map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`color-swatch ${folderColor === color ? 'active' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFolderColor(color)}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="folder-modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setShowFolderModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSaveFolder}
                disabled={!folderName.trim() || isSavingFolder}
              >
                {isSavingFolder ? 'Saving...' : (editingFolder ? 'Save' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div 
          className="project-context-menu"
          style={{ 
            position: 'fixed', 
            left: contextMenu.x, 
            top: contextMenu.y,
            zIndex: 1000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button onClick={() => { handleProjectClick(contextMenu.project); setContextMenu(null); }}>
            <OpenIcon /> Open
          </button>
          <button onClick={() => handleRenameClick(contextMenu.project)}>
            <EditIcon /> Rename
          </button>
          <button onClick={() => handleMoveClick(contextMenu.project)}>
            <FolderIcon color="currentColor" size={14} /> Move to Folder
          </button>
          <div className="context-menu-divider" />
          <button onClick={() => { 
            setPublishingProject(contextMenu.project); 
            setShowPublishModal(true); 
            setContextMenu(null); 
          }}>
            <GlobeIcon /> {contextMenu.project.is_published ? 'Update Publish' : 'Publish'}
          </button>
          <div className="context-menu-divider" />
          <button onClick={() => handleDeleteClick(contextMenu.project)} className="danger">
            <TrashIcon /> Delete
          </button>
        </div>
      )}

      {/* Rename Modal */}
      {showRenameModal && (
        <div className="folder-modal-overlay" {...overlayDismissProps(() => setShowRenameModal(false))}>
          <div className="folder-modal" onClick={e => e.stopPropagation()}>
            <h2>Rename Project</h2>
            
            <div className="folder-modal-form">
              <div className="form-group">
                <label>Project Name</label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="Project name"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleRenameSubmit()}
                />
              </div>
            </div>

            <div className="folder-modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setShowRenameModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleRenameSubmit}
                disabled={!newProjectName.trim() || isRenaming}
              >
                {isRenaming ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move to Folder Modal */}
      {showMoveModal && (
        <div className="folder-modal-overlay" {...overlayDismissProps(() => setShowMoveModal(false))}>
          <div className="folder-modal" onClick={e => e.stopPropagation()}>
            <h2>Move to Folder</h2>
            <p className="modal-subtitle">
              Moving {movingProjects.length} project{movingProjects.length > 1 ? 's' : ''}
            </p>
            
            <div className="move-folder-list">
              <button 
                className="move-folder-option"
                onClick={() => handleMoveToFolder('uncategorized')}
                disabled={isMoving}
              >
                <FolderIcon color="#6b7280" />
                <span>Uncategorized (no folder)</span>
              </button>
              {folders.map(folder => (
                <button 
                  key={folder.id}
                  className="move-folder-option"
                  onClick={() => handleMoveToFolder(folder.id)}
                  disabled={isMoving}
                >
                  <FolderIcon color={folder.color || '#f59e0b'} />
                  <span>{folder.name}</span>
                </button>
              ))}
            </div>

            <div className="folder-modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setShowMoveModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="folder-modal-overlay" {...overlayDismissProps(() => setConfirmModal(null))}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <div className={`confirm-icon ${confirmModal.danger ? 'danger' : ''}`}>
              {confirmModal.danger ? <WarningIcon /> : <QuestionIcon />}
            </div>
            <h2>{confirmModal.title}</h2>
            <p className="confirm-message">{confirmModal.message}</p>
            
            <div className="confirm-modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setConfirmModal(null)}
              >
                Cancel
              </button>
              <button 
                className={`btn-primary ${confirmModal.danger ? 'danger' : ''}`}
                onClick={confirmModal.onConfirm}
              >
                {confirmModal.danger ? 'Delete' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Publish Modal */}
      <PublishModal
        projectId={publishingProject?.aws_project_id || publishingProject?.id}
        projectName={publishingProject?.display_name || publishingProject?.name}
        isOpen={showPublishModal}
        onClose={() => {
          setShowPublishModal(false);
          setPublishingProject(null);
          loadProjects(); // Refresh to show updated publish status
        }}
        initialData={{
          subdomain: publishingProject?.subdomain,
          visibility: publishingProject?.visibility,
          is_published: publishingProject?.is_published,
          preview_url: publishingProject?.preview_url
        }}
      />
    </div>
  );
}

// Icons
function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19"/>
      <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}

function ProjectIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
      <line x1="3" y1="9" x2="21" y2="9"/>
      <line x1="9" y1="21" x2="9" y2="9"/>
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>
  );
}

function FolderIcon({ color = '#60D2CC', size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1.5">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
  );
}

function DragIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="9" cy="6" r="1.5"/>
      <circle cx="15" cy="6" r="1.5"/>
      <circle cx="9" cy="12" r="1.5"/>
      <circle cx="15" cy="12" r="1.5"/>
      <circle cx="9" cy="18" r="1.5"/>
      <circle cx="15" cy="18" r="1.5"/>
    </svg>
  );
}

function CheckboxIcon({ checked }) {
  if (checked) {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="18" height="18" rx="3" fill="#60D2CC" />
        <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="3"/>
    </svg>
  );
}

function OpenIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
      <polyline points="15 3 21 3 21 9"/>
      <line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  );
}

function QuestionIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  );
}

export default BuilderProjectsGrid;
